import React from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { TabsContent } from '@/components/ui/tabs';
import { Plus, X } from 'lucide-react';

const WEEKDAYS = [
  [0, 'الإثنين', 'Mon'], [1, 'الثلاثاء', 'Tue'], [2, 'الأربعاء', 'Wed'],
  [3, 'الخميس', 'Thu'], [4, 'الجمعة', 'Fri'], [5, 'السبت', 'Sat'], [6, 'الأحد', 'Sun']
];

// ── Time parsing & hours math ────────────────────────────────────────
// HTML <input type="time"> always emits 24-hour strings ("HH:MM") regardless
// of the browser's locale display (which may show AM/PM). We parse that.
const parseHHMM = (s) => {
  if (!s || typeof s !== 'string') return null;
  const m = s.match(/^(\d{1,2}):(\d{2})/);
  if (!m) return null;
  const h = parseInt(m[1], 10);
  const mm = parseInt(m[2], 10);
  if (isNaN(h) || isNaN(mm) || h < 0 || h > 23 || mm < 0 || mm > 59) return null;
  return h * 60 + mm;
};

const intervalMinutes = (start, end) => {
  const s = parseHHMM(start);
  const e = parseHHMM(end);
  if (s == null || e == null) return 0;
  let mins = e - s;
  if (mins <= 0) mins += 24 * 60; // overnight
  return mins;
};

const hoursFromOverride = (ov = {}) => {
  if (ov.flexible) return Number(ov.target_hours || 0);
  let mins = intervalMinutes(ov.start_time, ov.end_time);
  if (ov.start_time2 && ov.end_time2) {
    mins += intervalMinutes(ov.start_time2, ov.end_time2);
  }
  return Math.round((mins / 60) * 100) / 100;
};

export default function ScheduleTab({ formData, setFormData, language }) {
  const isAr = language === 'ar';
  const cs = formData.custom_schedule || {};
  const overrides = cs.day_overrides || {};
  const restDays = cs.rest_days || [];

  const updateCs = (patch) =>
    setFormData(prev => ({ ...prev, custom_schedule: { ...prev.custom_schedule, ...patch } }));

  const setOverride = (idx, mut) => setFormData(prev => {
    const cur = prev.custom_schedule || {};
    const k = String(idx);
    const existing = (cur.day_overrides || {})[k] || {};
    const next = mut(existing);
    if (next == null) {
      const all = { ...(cur.day_overrides || {}) };
      delete all[k];
      return { ...prev, custom_schedule: { ...cur, day_overrides: all } };
    }
    next.hours = hoursFromOverride(next);
    return {
      ...prev,
      custom_schedule: { ...cur, day_overrides: { ...(cur.day_overrides || {}), [k]: next } },
    };
  });

  const toggleRestDay = (idx) => setFormData(prev => {
    const list = prev.custom_schedule?.rest_days || [];
    const next = list.includes(idx) ? list.filter(d => d !== idx) : [...list, idx];
    return { ...prev, custom_schedule: { ...prev.custom_schedule, rest_days: next } };
  });

  const toggleOverride = (idx) => {
    const k = String(idx);
    if (overrides[k]) {
      setOverride(idx, () => null);
    } else {
      setOverride(idx, () => ({
        start_time: cs.start_time || '08:00',
        end_time: cs.end_time || '16:00',
      }));
    }
  };

  const toggleFlexible = (idx) => setOverride(idx, (ov) =>
    ov.flexible
      ? { start_time: cs.start_time || '08:00', end_time: cs.end_time || '16:00' }
      : { flexible: true, target_hours: Number(ov.hours || cs.daily_hours || 8) }
  );

  const setField = (idx, field, value) => setOverride(idx, (ov) => ({ ...ov, [field]: value }));

  const addSecondShift = (idx) => setOverride(idx, (ov) => ({
    ...ov,
    start_time2: '17:00',
    end_time2: '21:00',
  }));

  const removeSecondShift = (idx) => setOverride(idx, (ov) => {
    const { start_time2, end_time2, ...rest } = ov;
    return rest;
  });

  return (
    <TabsContent value="schedule" className="space-y-3 mt-3 h-[480px] overflow-y-auto pe-1">
      <div className="bg-white rounded-lg border border-[#C4BFF0] p-3 space-y-3">
        <p className="text-xs font-semibold text-[#655dc6]/70 tracking-wide">
          {isAr ? 'جدول الدوام للموظف' : 'Employee Work Schedule'}
        </p>

        <div className="bg-[#E8E6F5] rounded-lg p-3 flex items-center justify-between">
          <span className="text-[11px] text-[#655dc6]/80">
            {isAr
              ? 'افتراضياً يُستخدم جدول القسم. فعّل الجدول المخصص لتجاوزه لهذا الموظف فقط.'
              : 'By default the department schedule applies. Enable the custom schedule to override it for this employee only.'}
          </span>
          <div className="flex items-center gap-2 ms-2 shrink-0">
            <input
              type="checkbox"
              id="use_custom_sched"
              checked={!!formData.use_custom_schedule}
              onChange={(e) => setFormData(prev => ({ ...prev, use_custom_schedule: e.target.checked }))}
              className="h-4 w-4 rounded border-[#C4BFF0] text-[#655dc6] focus:ring-[#655dc6]"
              data-testid="input-use-custom-schedule"
            />
            <Label htmlFor="use_custom_sched" className="text-xs text-[#655dc6] cursor-pointer">{isAr ? 'تفعيل' : 'Enable'}</Label>
          </div>
        </div>

        {/* ─── Manual attendance toggle ───
            Allows admin to flag specific employees (e.g., field reps,
            employees whose GPS is unreliable on-site) to punch attendance
            without geofence/GPS — just by entering the time manually. */}
        <div className="bg-emerald-50/60 border border-emerald-200/70 rounded-lg p-3 flex items-center justify-between">
          <div className="flex flex-col gap-0.5">
            <span className="text-[11px] font-semibold text-emerald-900">
              {isAr ? 'السماح بإدخال الحضور يدوياً' : 'Allow Manual Attendance Entry'}
            </span>
            <span className="text-[10px] text-emerald-800/70">
              {isAr
                ? 'يمكّن الموظف من تسجيل الحضور والانصراف بإدخال الوقت يدوياً (بدون GPS / موقع)، فقط لليوم الحالي.'
                : 'Lets this employee punch in/out by entering time manually (no GPS), today only.'}
            </span>
          </div>
          <div className="flex items-center gap-2 ms-2 shrink-0">
            <input
              type="checkbox"
              id="manual_attendance_allowed"
              checked={!!formData.manual_attendance_allowed}
              onChange={(e) => setFormData(prev => ({ ...prev, manual_attendance_allowed: e.target.checked }))}
              className="h-4 w-4 rounded border-emerald-300 text-emerald-700 focus:ring-emerald-600"
              data-testid="input-manual-attendance-allowed"
            />
            <Label htmlFor="manual_attendance_allowed" className="text-xs text-emerald-900 cursor-pointer">
              {isAr ? 'تفعيل' : 'Enable'}
            </Label>
          </div>
        </div>

        {/* ─── Multi-punch override ───
            Lets the employee clock-in & clock-out MULTIPLE times in the
            same day even if their schedule has only one shift (e.g. they
            step out for a personal errand and come back). Without this,
            the system blocks the second clock-in attempt. */}
        <div className="bg-violet-50/60 border border-violet-200/70 rounded-lg p-3 flex items-center justify-between">
          <div className="flex flex-col gap-0.5">
            <span className="text-[11px] font-semibold text-violet-900">
              {isAr ? 'السماح بأكثر من تسجيل دخول/خروج في اليوم' : 'Allow multiple punches per day'}
            </span>
            <span className="text-[10px] text-violet-800/70">
              {isAr
                ? 'يمكّن الموظف من تسجيل الحضور والانصراف عدة مرات في نفس اليوم (مثلاً للخروج لقضاء أمر ثم العودة) رغم أن جدوله يحوي وردية واحدة.'
                : 'Lets this employee clock in/out multiple times in the same day (e.g. step out for an errand) even when scheduled for one shift.'}
            </span>
          </div>
          <div className="flex items-center gap-2 ms-2 shrink-0">
            <input
              type="checkbox"
              id="allow_multiple_punches"
              checked={!!formData.allow_multiple_punches}
              onChange={(e) => setFormData(prev => ({ ...prev, allow_multiple_punches: e.target.checked }))}
              className="h-4 w-4 rounded border-violet-300 text-violet-700 focus:ring-violet-600"
              data-testid="input-allow-multiple-punches"
            />
            <Label htmlFor="allow_multiple_punches" className="text-xs text-violet-900 cursor-pointer">
              {isAr ? 'تفعيل' : 'Enable'}
            </Label>
          </div>
        </div>

        {formData.use_custom_schedule && (
          <div className="space-y-3" data-testid="custom-schedule-fields">
            <div className="bg-amber-50/40 border border-amber-200/60 rounded-md p-2.5 space-y-2.5">
              <p className="text-[10px] font-semibold text-amber-900 leading-tight">
                {isAr
                  ? '⚙️ الإعداد الافتراضي (للأيام بدون استثناءات في الأسفل)'
                  : '⚙️ Default settings (for days without an override below)'}
              </p>
              <div className="grid grid-cols-3 gap-3" dir={isAr ? 'rtl' : 'ltr'}>
                <div className="space-y-1">
                  <Label className="text-xs text-start block">{isAr ? 'عدد الساعات' : 'Daily Hours'}</Label>
                  <Input type="number" step="0.5" min="1" max="24" dir="ltr" className="text-center"
                    value={cs.daily_hours ?? 8}
                    onChange={(e) => updateCs({ daily_hours: parseFloat(e.target.value || 0) })}
                    data-testid="input-cs-hours" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-start block">{isAr ? 'من (بداية الدوام)' : 'From'}</Label>
                  <Input type="time" lang="en-GB" dir="ltr" className="text-center"
                    value={cs.start_time ?? '08:00'}
                    onChange={(e) => updateCs({ start_time: e.target.value })}
                    data-testid="input-cs-start" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-start block">{isAr ? 'إلى (نهاية الدوام)' : 'To'}</Label>
                  <Input type="time" lang="en-GB" dir="ltr" className="text-center"
                    value={cs.end_time ?? '16:00'}
                    onChange={(e) => updateCs({ end_time: e.target.value })}
                    data-testid="input-cs-end" />
                </div>
              </div>
              {Object.keys(overrides).length > 0 && (
                <p className="text-[10px] text-amber-800 bg-amber-100/60 rounded px-2 py-1 leading-relaxed">
                  {isAr
                    ? `🔔 يوجد ${Object.keys(overrides).length} يوم باستثناءات بالأسفل — هذه الأيام لا تستخدم الإعداد الافتراضي أعلاه.`
                    : `🔔 ${Object.keys(overrides).length} day(s) have overrides below — they will NOT use the defaults above.`}
                </p>
              )}
            </div>

            <div className="space-y-1">
              <Label className="text-xs text-start block">{isAr ? 'أيام الراحة' : 'Rest Days'}</Label>
              <div className="flex flex-wrap gap-1.5">
                {WEEKDAYS.map(([idx, ar, en]) => {
                  const selected = restDays.includes(idx);
                  return (
                    <button key={idx} type="button" onClick={() => toggleRestDay(idx)}
                      className={`px-2.5 py-1 rounded-md text-[11px] font-medium transition-all ${
                        selected ? 'bg-[#655dc6] text-white shadow' : 'bg-white border border-[#C4BFF0] text-[#655dc6] hover:bg-[#E8E6F5]'
                      }`}
                      data-testid={`cs-rest-${idx}`}>
                      {isAr ? ar : en}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label className="text-xs text-start block font-semibold">
                  {isAr ? 'استثناءات الأيام (تتجاوز الافتراضي أعلاه)' : 'Day Overrides (override defaults above)'}
                </Label>
                <span className="text-[10px] text-[#655dc6]/60">{isAr ? 'اختياري' : 'optional'}</span>
              </div>
              <div className="rounded-md border border-[#C4BFF0] overflow-hidden" data-testid="cs-day-overrides">
                {WEEKDAYS.map(([idx, ar, en], i) => {
                  if (restDays.includes(idx)) return null;
                  const k = String(idx);
                  const ov = overrides[k];
                  const enabled = !!ov;
                  const flexible = !!(ov && ov.flexible);
                  const hasSecond = !!(ov && ov.start_time2 && ov.end_time2);
                  return (
                    <div key={idx}
                      className={`px-3 py-2 ${i > 0 ? 'border-t border-[#E8E6F5]' : ''} ${enabled ? 'bg-[#FAF9FF]' : ''}`}
                      data-testid={`cs-day-row-${idx}`}>
                      <div className="flex items-center gap-3 flex-wrap">
                        <div className="w-16 text-xs font-semibold text-[#655dc6]">{isAr ? ar : en}</div>
                        <label className="flex items-center gap-1.5 cursor-pointer">
                          <input type="checkbox" checked={enabled}
                            onChange={() => toggleOverride(idx)}
                            className="h-4 w-4 rounded border-[#C4BFF0] text-[#655dc6]"
                            data-testid={`cs-day-enable-${idx}`} />
                          <span className="text-xs text-[#655dc6]/80">{isAr ? 'مخصّص' : 'Override'}</span>
                        </label>
                        {enabled && (
                          <label className="flex items-center gap-1.5 cursor-pointer">
                            <input type="checkbox" checked={flexible}
                              onChange={() => toggleFlexible(idx)}
                              className="h-4 w-4 rounded border-[#C4BFF0] text-[#655dc6]"
                              data-testid={`cs-day-flexible-${idx}`} />
                            <span className="text-xs text-[#655dc6]/80">{isAr ? 'مرن' : 'Flexible'}</span>
                          </label>
                        )}
                        {enabled && !flexible && (
                          <span className="text-[10px] font-mono text-emerald-700 bg-emerald-50 px-1.5 py-0.5 rounded ms-auto">
                            {hoursFromOverride(ov)}h
                          </span>
                        )}
                      </div>

                      {enabled && (
                        <div className="mt-2 ps-[76px] space-y-2">
                          {flexible ? (
                            <div className="flex items-center gap-2 text-xs">
                              <span className="text-[#655dc6]/70">{isAr ? 'الساعات المستهدفة:' : 'Target Hours:'}</span>
                              <Input type="number" step="0.5" min="0" max="24" dir="ltr"
                                value={ov.target_hours ?? 4}
                                onChange={(e) => setField(idx, 'target_hours', parseFloat(e.target.value || 0))}
                                className="h-7 w-20 text-center text-xs"
                                data-testid={`cs-day-target-${idx}`} />
                            </div>
                          ) : (
                            <>
                              {/* Shift 1 — force LTR so time order is always Start → End visually */}
                              <div className="flex items-center gap-2 flex-wrap" dir="ltr">
                                <span className="text-[10px] font-semibold text-[#655dc6]/60 w-14" dir={isAr ? 'rtl' : 'ltr'}>
                                  {isAr ? 'الدوام 1' : 'Shift 1'}
                                </span>
                                <Input type="time" lang="en-GB" dir="ltr"
                                  value={ov.start_time || '08:00'}
                                  onChange={(e) => setField(idx, 'start_time', e.target.value)}
                                  className="h-7 w-28 text-center text-xs"
                                  data-testid={`cs-day-start-${idx}`} />
                                <span className="text-[#655dc6]/60">→</span>
                                <Input type="time" lang="en-GB" dir="ltr"
                                  value={ov.end_time || '12:00'}
                                  onChange={(e) => setField(idx, 'end_time', e.target.value)}
                                  className="h-7 w-28 text-center text-xs"
                                  data-testid={`cs-day-end-${idx}`} />
                                {!hasSecond && (
                                  <button type="button" onClick={() => addSecondShift(idx)}
                                    className="ms-auto flex items-center gap-1 text-[10px] text-[#655dc6] hover:bg-[#E8E6F5] px-1.5 py-0.5 rounded"
                                    data-testid={`cs-day-add-shift2-${idx}`}>
                                    <Plus className="h-3 w-3" />
                                    <span dir={isAr ? 'rtl' : 'ltr'}>{isAr ? 'إضافة دوام ثاني' : 'Add Second Shift'}</span>
                                  </button>
                                )}
                              </div>
                              {/* Shift 2 */}
                              {hasSecond && (
                                <div className="flex items-center gap-2 flex-wrap" dir="ltr">
                                  <span className="text-[10px] font-semibold text-[#655dc6]/60 w-14" dir={isAr ? 'rtl' : 'ltr'}>
                                    {isAr ? 'الدوام 2' : 'Shift 2'}
                                  </span>
                                  <Input type="time" lang="en-GB" dir="ltr"
                                    value={ov.start_time2 || '17:00'}
                                    onChange={(e) => setField(idx, 'start_time2', e.target.value)}
                                    className="h-7 w-28 text-center text-xs"
                                    data-testid={`cs-day-start2-${idx}`} />
                                  <span className="text-[#655dc6]/60">→</span>
                                  <Input type="time" lang="en-GB" dir="ltr"
                                    value={ov.end_time2 || '21:00'}
                                    onChange={(e) => setField(idx, 'end_time2', e.target.value)}
                                    className="h-7 w-28 text-center text-xs"
                                    data-testid={`cs-day-end2-${idx}`} />
                                  <button type="button" onClick={() => removeSecondShift(idx)}
                                    className="text-rose-600 hover:bg-rose-50 px-1 py-0.5 rounded"
                                    title={isAr ? 'حذف الدوام الثاني' : 'Remove second shift'}
                                    data-testid={`cs-day-remove-shift2-${idx}`}>
                                    <X className="h-3.5 w-3.5" />
                                  </button>
                                </div>
                              )}
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs text-start block">{isAr ? 'فترة السماح (دقيقة)' : 'Grace (min)'}</Label>
                <Input type="number" min="0" max="240" dir="ltr" className="text-center"
                  value={cs.grace_minutes ?? 30}
                  onChange={(e) => updateCs({ grace_minutes: parseInt(e.target.value || 0) })}
                  data-testid="input-cs-grace" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-start block">{isAr ? 'احتساب جزاء التأخير' : 'Late Penalty'}</Label>
                <div className="flex items-center gap-2 pt-1">
                  <input type="checkbox" id="cs_penalty"
                    checked={!!cs.late_penalty_enabled}
                    onChange={(e) => updateCs({ late_penalty_enabled: e.target.checked })}
                    className="h-4 w-4 rounded border-[#C4BFF0] text-[#655dc6] focus:ring-[#655dc6]"
                    data-testid="input-cs-penalty" />
                  <Label htmlFor="cs_penalty" className="text-xs text-[#655dc6] cursor-pointer">
                    {cs.late_penalty_enabled ? (isAr ? 'مفعّل' : 'Enabled') : (isAr ? 'معطّل' : 'Disabled')}
                  </Label>
                </div>
              </div>
            </div>

            <div className="bg-[#E8E6F5] rounded-md p-2 text-[10px] text-[#655dc6]/70 leading-relaxed">
              {isAr
                ? '💡 ترتيب الأولوية: الاستثناءات بالأسفل > الإعداد الافتراضي بالأعلى > جدول القسم. يمكنك تحديد دوامين منفصلين في نفس اليوم (مثلاً صباحي ومسائي)، ويجمع النظام ساعاتهما لاحتساب اليوم.'
                : '💡 Priority: Day Overrides (below) > Default Settings (above) > Department Schedule. You can configure two separate shifts per day; the system sums both.'}
            </div>
          </div>
        )}
      </div>
    </TabsContent>
  );
}
